import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';

const Footer = () => {
  const fadeInVariant = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
  };

  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-gradient-to-b from-green-50 via-gray-100 to-gray-700 text-white relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0 bg-gradient-to-br from-green-500/20 via-transparent to-green-500/10"></div>
        <svg className="absolute top-0 left-0 w-full h-full" preserveAspectRatio="none" viewBox="0 0 1200 800">
          <defs>
            <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
              <path d="M 40 0 L 0 0 0 40" fill="none" stroke="currentColor" strokeWidth="1" />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#grid)" />
        </svg>
      </div>

      <div className="relative">
        {/* Main Footer Content */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 sm:pt-16 pb-6 sm:pb-8">

          {/* Mobile Layout - Stacked */}
          <div className="block md:hidden space-y-8">

            {/* Company Info & CEO - Mobile */}
            <motion.div
              variants={fadeInVariant}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
            >
              {/* Logo */}
              <div className="flex justify-center mb-8">
                <img
                  src="/MGS-Logo.png"
                  alt="MGS Gebäudereinigung Logo"
                  className="h-16 w-auto"
                />
              </div>

              {/* Company Description */}
              <div className="text-center mb-8">
                <h3 className="text-xl font-bold text-gray-800 mb-4">MGS Gebäudereinigung</h3>
                <p className="text-gray-600 leading-relaxed mb-6">
                  Professionelle Gebäudereinigung und Facility Services in Landshut und Landkreis.
                  Wir sorgen für perfekte Sauberkeit mit modernster Technik und traditioneller Handwerkskunst.
                </p>

                {/* CEO Quote */}
                <div className="bg-white/80 rounded-xl p-6 border border-gray-200">
                  <blockquote className="text-gray-700 italic mb-4">
                    "Sauberkeit ist nicht nur unser Beruf, sondern unsere Leidenschaft.
                    Wir sorgen dafür, dass Ihre Räume nicht nur sauber, sondern auch hygienisch und einladend sind."
                  </blockquote>
                  <div className="text-center">
                    <div className="text-gray-800 font-semibold">Gani Morina</div>
                    <div className="text-gray-600 text-sm">Geschäftsführer</div>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Navigation - Mobile */}
            <motion.div
              variants={fadeInVariant}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              className="grid grid-cols-2 gap-6"
            >
              <div>
                <h4 className="text-gray-800 font-bold mb-4">Services</h4>
                <ul className="space-y-2">
                  <li><Link to="/services/bueroreinigung" className="text-gray-600 hover:text-green-600 transition-colors">Büroreinigung</Link></li>
                  <li><Link to="/services/treppenhausreinigung" className="text-gray-600 hover:text-green-600 transition-colors">Treppenhausreinigung</Link></li>
                  <li><Link to="/services/fensterglasreinigung" className="text-gray-600 hover:text-green-600 transition-colors">Fensterglasreinigung</Link></li>
                  <li><Link to="/services/fliesenlegen" className="text-gray-600 hover:text-green-600 transition-colors">Fliesenlegen</Link></li>
                </ul>
              </div>
              <div>
                <h4 className="text-gray-800 font-bold mb-4">Unternehmen</h4>
                <ul className="space-y-2">
                  <li><Link to="/ueber-uns" className="text-gray-600 hover:text-green-600 transition-colors">Über Uns</Link></li>
                  <li><Link to="/kontakt" className="text-gray-600 hover:text-green-600 transition-colors">Kontakt</Link></li>
                  <li><Link to="/impressum" className="text-gray-600 hover:text-green-600 transition-colors">Impressum</Link></li>
                  <li><Link to="/datenschutz" className="text-gray-600 hover:text-green-600 transition-colors">Datenschutz</Link></li>
                </ul>
              </div>
            </motion.div>

            {/* Contact Info - Mobile */}
            <motion.div
              variants={fadeInVariant}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              className="text-center"
            >
              <h4 className="text-gray-800 font-bold mb-4">Kontakt</h4>
              <div className="space-y-2 text-gray-600">
                <div className="flex items-center justify-center">
                  <svg className="w-5 h-5 mr-2 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  <span>info@mgs-services.de</span>
                </div>
                <div className="flex items-center justify-center">
                  <svg className="w-5 h-5 mr-2 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                  <span>+49 871 14241333</span>
                </div>
                <div className="flex items-center justify-center">
                  <svg className="w-5 h-5 mr-2 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  <span>Landshut & Landkreis</span>
                </div>
              </div>
            </motion.div>

            {/* Social & Copyright - Mobile */}
            <motion.div
              variants={fadeInVariant}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              className="text-center pt-6 border-t border-gray-300"
            >
              <div className="text-gray-600 text-sm mb-4">
                © {currentYear} MGS Gebäudereinigung. Alle Rechte vorbehalten.
              </div>
              <div className="flex justify-center space-x-4">
                <Link to="/agb" className="text-gray-600 hover:text-green-600 text-sm transition-colors">AGB</Link>
                <Link to="/cookies" className="text-gray-600 hover:text-green-600 text-sm transition-colors">Cookies</Link>
              </div>
            </motion.div>
          </div>

          {/* Desktop Layout - Grid */}
          <div className="hidden md:grid md:grid-cols-12 gap-8 lg:gap-12">

            {/* Company Info & CEO - Desktop */}
            <motion.div
              variants={fadeInVariant}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              className="md:col-span-4 lg:col-span-5"
            >
              {/* Logo */}
              <div className="mb-8">
                <img
                  src="/MGS-Logo.png"
                  alt="MGS Gebäudereinigung Logo"
                  className="h-20 w-auto"
                />
              </div>

              {/* Company Description */}
              <div className="mb-8">
                <h3 className="text-2xl font-bold text-gray-800 mb-4">MGS Gebäudereinigung</h3>
                <p className="text-gray-600 leading-relaxed mb-6">
                  Professionelle Gebäudereinigung und Facility Services in Landshut und Landkreis.
                  Wir sorgen für perfekte Sauberkeit mit modernster Technik und traditioneller Handwerkskunst.
                </p>

                {/* CEO Quote */}
                <div className="bg-white/80 rounded-xl p-6 border border-gray-200">
                  <blockquote className="text-gray-700 italic mb-4">
                    "Sauberkeit ist nicht nur unser Beruf, sondern unsere Leidenschaft.
                    Wir sorgen dafür, dass Ihre Räume nicht nur sauber, sondern auch hygienisch und einladend sind."
                  </blockquote>
                  <div className="text-center">
                    <div className="text-gray-800 font-semibold">Gani Morina</div>
                    <div className="text-gray-600 text-sm">Geschäftsführer</div>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Navigation - Desktop */}
            <motion.div
              variants={fadeInVariant}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              className="md:col-span-3 lg:col-span-2"
            >
              <h4 className="text-gray-800 font-bold mb-6 text-lg">Services</h4>
              <ul className="space-y-3">
                <li><Link to="/services/bueroreinigung" className="text-gray-600 hover:text-green-600 transition-colors">Büroreinigung</Link></li>
                <li><Link to="/services/treppenhausreinigung" className="text-gray-600 hover:text-green-600 transition-colors">Treppenhausreinigung</Link></li>
                <li><Link to="/services/fensterglasreinigung" className="text-gray-600 hover:text-green-600 transition-colors">Fensterglasreinigung</Link></li>
                <li><Link to="/services/fliesenlegen" className="text-gray-600 hover:text-green-600 transition-colors">Fliesenlegen</Link></li>
                <li><Link to="/services/gebaeudereinigung" className="text-gray-600 hover:text-green-600 transition-colors">Gebäudereinigung</Link></li>
              </ul>
            </motion.div>

            <motion.div
              variants={fadeInVariant}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              className="md:col-span-3 lg:col-span-2"
            >
              <h4 className="text-gray-800 font-bold mb-6 text-lg">Unternehmen</h4>
              <ul className="space-y-3">
                <li><Link to="/ueber-uns" className="text-gray-600 hover:text-green-600 transition-colors">Über Uns</Link></li>
                <li><Link to="/kontakt" className="text-gray-600 hover:text-green-600 transition-colors">Kontakt</Link></li>
                <li><Link to="/impressum" className="text-gray-600 hover:text-green-600 transition-colors">Impressum</Link></li>
                <li><Link to="/datenschutz" className="text-gray-600 hover:text-green-600 transition-colors">Datenschutz</Link></li>
              </ul>
            </motion.div>

            {/* Contact Info - Desktop */}
            <motion.div
              variants={fadeInVariant}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              className="md:col-span-2 lg:col-span-3"
            >
              <h4 className="text-gray-800 font-bold mb-6 text-lg">Kontakt</h4>
              <div className="space-y-4 text-gray-600">
                <div className="flex items-center">
                  <svg className="w-5 h-5 mr-3 text-green-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  <a href="mailto:info@mgs-services.de" className="hover:text-green-600 transition-colors">info@mgs-services.de</a>
                </div>
                <div className="flex items-center">
                  <svg className="w-5 h-5 mr-3 text-green-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                  <a href="tel:+4917624719918" className="hover:text-green-600 transition-colors">+49 871 14241333</a>
                </div>
                <div className="flex items-center">
                  <svg className="w-5 h-5 mr-3 text-green-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  <span>Landshut & Landkreis</span>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Bottom Bar */}
          <motion.div
            variants={fadeInVariant}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="mt-12 pt-8 border-t border-gray-300"
          >
            <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
              <div className="text-gray-600 text-sm">
                © {currentYear} MGS Gebäudereinigung. Alle Rechte vorbehalten.
              </div>
              <div className="flex space-x-6">
                <Link to="/agb" className="text-gray-600 hover:text-green-600 text-sm transition-colors">AGB</Link>
                <Link to="/cookies" className="text-gray-600 hover:text-green-600 text-sm transition-colors">Cookies</Link>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;